import { nombreEnTexte } from './nombre.js';

/**
 * LE CALCULATEUR DE DOSE, en calculs PURS : aucun React, aucun SDK, testes par
 * `doses.test.mjs`. La regle est celle des tableurs de clinique (Universite de
 * Bristol, fiche CSL_A07 ; calculateur d'urgence de Colorado State University) :
 *
 *   poids (kg) x dose (mg/kg)          = quantite de substance (mg)
 *   quantite (mg) / concentration      = quantite de produit (ml, comprimes)
 *
 * Une dose en µg/kg se convertit en mg avant de diviser par la concentration,
 * qui est toujours en mg par unite du produit (champ `concentration` du
 * Catalogue).
 */

/** Le profil d'une posologie : la dose de l'adulte sain, de l'animal age, de l'animal malade (ASA III et plus). */
export type Profil = 'Standard' | 'Senior' | 'Fragile';

/** L'etat que le veterinaire choisit : sain (ASA I et II) ou malade (ASA III et plus). */
export type Etat = 'Sain' | 'Malade';

/** Une posologie du Catalogue, lue avec son produit. */
export interface PosologieDuCalcul {
  id: string;
  libelle: string;
  /** Le calcul qui la regroupe (« Anesthesie », « Methadone »). */
  calculId: string;
  profil: Profil;
  productId: string;
  produit: string;
  /** L'unite du produit (ml, comprime) : celle de la quantite a administrer. */
  unite: string;
  /** Les mg par unite du produit ; `null` quand le Catalogue ne la donne pas. */
  concentration: number | null;
  indication: string;
  especes: readonly string[];
  dose: number;
  doseMax: number | null;
  doseUnit: 'mg/kg' | 'µg/kg';
  route: string;
  note: string;
  source: string;
}

export interface ResultatDuCalcul {
  /** La quantite de substance, dans l'unite de la dose (mg ou µg), et son maximum. */
  substance: number;
  substanceMax: number | null;
  /** La quantite de produit a administrer, et son maximum ; `null` sans concentration. */
  produit: number | null;
  produitMax: number | null;
}

/** Les etapes, dans l'ordre d'une anesthesie, puis le reste. */
export const ORDRE_DES_ETAPES: readonly string[] = [
  'Prémédication',
  'Sédation',
  'Induction',
  'Analgésie',
  'Anti-inflammatoire',
  'Antibiotique',
  'Urgence',
  'Antagoniste',
  'Autre',
];

/** Un poids saisi (« 4,5 », « 15 ») en kg ; `null` s'il n'est pas un nombre positif. */
export function lirePoids(saisie: string): number | null {
  const nombre = Number(saisie.trim().replace(',', '.'));
  return saisie.trim() !== '' && Number.isFinite(nombre) && nombre > 0 ? nombre : null;
}

export function calculerDose(
  poids: number,
  posologie: Pick<PosologieDuCalcul, 'dose' | 'doseMax' | 'doseUnit' | 'concentration'>,
): ResultatDuCalcul {
  const substance = poids * posologie.dose;
  const substanceMax = posologie.doseMax === null ? null : poids * posologie.doseMax;
  const enMg = (q: number): number => (posologie.doseUnit === 'µg/kg' ? q / 1000 : q);
  const c = posologie.concentration;
  const valide = c !== null && Number.isFinite(c) && c > 0;
  return {
    substance,
    substanceMax,
    produit: valide ? enMg(substance) / c : null,
    produitMax: valide && substanceMax !== null ? enMg(substanceMax) / c : null,
  };
}

/** « 0,04 mg/kg », « 5 à 10 mg/kg ». */
export function doseEnTexte(p: Pick<PosologieDuCalcul, 'dose' | 'doseMax' | 'doseUnit'>): string {
  return p.doseMax === null ? `${nombreEnTexte(p.dose)} ${p.doseUnit}` : `${nombreEnTexte(p.dose)} à ${nombreEnTexte(p.doseMax)} ${p.doseUnit}`;
}

/** « 0,8 mg », « 400 µg », « 20 à 40 mg ». */
export function substanceEnTexte(r: ResultatDuCalcul, doseUnit: PosologieDuCalcul['doseUnit']): string {
  const unite = doseUnit === 'µg/kg' ? 'µg' : 'mg';
  return r.substanceMax === null ? `${nombreEnTexte(r.substance)} ${unite}` : `${nombreEnTexte(r.substance)} à ${nombreEnTexte(r.substanceMax)} ${unite}`;
}

/** « 0,4 ml », « 0,5 à 1 comprimé » ; vide sans concentration. */
export function produitEnTexte(r: ResultatDuCalcul, unite: string): string {
  if (r.produit === null) return '';
  const quantite = r.produitMax === null ? nombreEnTexte(r.produit) : `${nombreEnTexte(r.produit)} à ${nombreEnTexte(r.produitMax)}`;
  return unite === '' ? quantite : `${quantite} ${unite}`;
}

/** L'age en annees (une decimale) au jour donne, depuis une naissance en date pure ; `null` si elle manque. */
export function ageEnAnnees(naissance: unknown, jour: Date): number | null {
  if (typeof naissance !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(naissance)) return null;
  let mois = (jour.getFullYear() - Number(naissance.slice(0, 4))) * 12 + (jour.getMonth() - (Number(naissance.slice(5, 7)) - 1));
  if (jour.getDate() < Number(naissance.slice(8, 10))) mois -= 1;
  return mois < 0 ? null : Math.round((mois / 12) * 10) / 10;
}

/** Le profil du patient : malade d'abord, puis l'age au-dela du seuil senior du calcul, sinon standard. */
export function profilDuPatient(age: number | null, ageSenior: number | null, etat: Etat): Profil {
  if (etat === 'Malade') return 'Fragile';
  return age !== null && ageSenior !== null && age >= ageSenior ? 'Senior' : 'Standard';
}

/** Les profils acceptes, du plus precis au plus general : un malade sans dose « Fragile » prend la dose senior, sinon standard. */
const REPLIS: Readonly<Record<Profil, readonly Profil[]>> = {
  Fragile: ['Fragile', 'Senior', 'Standard'],
  Senior: ['Senior', 'Standard'],
  Standard: ['Standard'],
};

export interface LigneDuCalcul extends PosologieDuCalcul {
  /** Vrai quand la ligne vient d'un profil plus general que celui du patient. */
  repli: boolean;
}

/**
 * LES LIGNES D'UN CALCUL pour ce patient : ses posologies de l'espece, une par
 * etape et par produit, au profil le plus precis disponible. Rangees dans
 * l'ordre de l'anesthesie.
 */
export function lignesDuCalcul(posologies: readonly PosologieDuCalcul[], calculId: string, espece: string, profil: Profil): LigneDuCalcul[] {
  const candidates = posologies.filter((p) => p.calculId === calculId && (p.especes.length === 0 || p.especes.includes(espece)));
  const cles = [...new Set(candidates.map((p) => `${p.indication}|${p.productId}`))];
  const lignes: LigneDuCalcul[] = [];
  for (const cle of cles) {
    const memes = candidates.filter((p) => `${p.indication}|${p.productId}` === cle);
    for (const voulu of REPLIS[profil]) {
      const trouvee = memes.find((p) => p.profil === voulu);
      if (trouvee !== undefined) {
        lignes.push({ ...trouvee, repli: voulu !== profil });
        break;
      }
    }
  }
  const rang = (etape: string): number => {
    const i = ORDRE_DES_ETAPES.indexOf(etape);
    return i === -1 ? ORDRE_DES_ETAPES.length : i;
  };
  return lignes.sort((a, b) => rang(a.indication) - rang(b.indication) || a.produit.localeCompare(b.produit, 'fr'));
}
